import {
  calculateTotalAdditionalSavings,
  getBeamBoxSavings,
  getEmaldoGridRewards,
  getProductAISavings,
  ProductAISavings,
} from '@bodilenergy/domain';

import { OfferProducts } from '~src/types';
import type { OfferSavings } from '~src/gql';

import { useEnergyOptions } from './useEnergyOptions';

type Offer = {
  expenses?: Array<{ title?: string; templateID?: number }>;
};

// Adapter to convert GraphQL savings to domain format
const adaptSavingsForDomain = (savings?: OfferSavings) => {
  if (!savings) return undefined;
  
  return {
    averageSavings: {
      totalPrice: savings.saved?.price ?? 0,
    },
    averagePriceAfter: {
      soldPrice: savings.after?.soldPrice,
    },
  } as any; // Using 'as any' to satisfy domain type expectations
};

export const useAdditionalOfferSavings = (
  offer?: Offer,
  products?: OfferProducts,
  savings?: OfferSavings,
  postalCode?: string | number,
  residenceId?: number
) => {
  const { energyOptions } = useEnergyOptions(residenceId);

  if (!products || !energyOptions) {
    return {
      additionalSavings: [] as ProductAISavings[],
      totalAdditionalSavings: 0,
      emaldoGridRewards: undefined,
      beamBoxSavings: undefined,
      productAISavings: undefined,
    };
  }

  const postalCodeNumber = typeof postalCode === 'string' ? parseInt(postalCode, 10) : postalCode;
  const adaptedSavings = adaptSavingsForDomain(savings);

  const emaldoGridRewards = getEmaldoGridRewards(products, postalCodeNumber, energyOptions);
  const beamBoxSavings = getBeamBoxSavings(offer ?? {}, postalCodeNumber, energyOptions);
  const productAISavings = getProductAISavings(offer ?? {}, products, adaptedSavings);

  const additionalSavings = [emaldoGridRewards, beamBoxSavings, productAISavings].filter(
    Boolean
  ) as ProductAISavings[];

  const totalAdditionalSavings = calculateTotalAdditionalSavings(
    offer ?? {},
    products,
    adaptedSavings,
    postalCodeNumber,
    energyOptions
  );

  return {
    additionalSavings,
    totalAdditionalSavings,
    emaldoGridRewards,
    beamBoxSavings,
    productAISavings,
  };
};
